import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { cpApi } from '../api/api';
import type {
  CpDashboardChartsPoint,
  CpDashboardChartsResponse,
  DashboardCurrency,
  DashboardGranularity,
  DashboardStatusValues,
  RequisiteWorkloadProfile,
  WorkloadSeriesPoints,
  WorkloadTf,
} from '../api/dashboard';
import { emptyVolumeByStatus } from '../api/dashboard';
import { BankFilter } from './dashboard/BankFilter';
import { ChartFrameToggle, DashboardCard, SegmentedControl } from './dashboard/DashboardCard';
import {
  LineChart,
  MULTI_LINE_PALETTE,
  StackedBarChart,
  StatusLegend,
  useStatusLegendState,
} from './dashboard/DashboardCharts';
import {
  formatDashboardNumber,
  formatDashboardPercent,
  formatDashboardUsdt,
} from './dashboard/formatters';
import { RequisiteProfileFilter } from './dashboard/RequisiteProfileFilter';
import { VolumeProfileLegend, WorkloadLegendHelpTip } from './dashboard/VolumeProfileLegend';
import { WorkloadTimeSeriesChart } from './dashboard/WorkloadTimeSeriesChart';
import {
  WorkloadModePicker,
  WorkloadPresetPicker,
  WorkloadTimeframePicker,
  WORKLOAD_PRESET_DAYS,
  WORKLOAD_PRESET_TF,
  type WorkloadChartMode,
  type WorkloadPreset,
} from './dashboard/WorkloadTimeframeBar';
import { useDashboardFilter } from '../context/useDashboardFilter';

type WorkloadMetric = 'amount_vnd' | 'request_count';
type WorkloadPointMap = Record<string, WorkloadSeriesPoints>;

const WORKLOAD_MAX_SERIES_BARS = 3000;
const WORKLOAD_LIVE_INTERVAL_MS = 20_000;
const WORKLOAD_TF_ORDER: WorkloadTf[] = ['1m', '5m', '15m', '30m', '1h', '4h', '1d'];
const MSK_OFFSET_SEC = 3 * 60 * 60;

function formatLocalDateParam(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseYmd(value: string): Date | null {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day);
  date.setHours(0, 0, 0, 0);
  return Number.isNaN(date.getTime()) ? null : date;
}

function alignWorkloadBucket(ts: number, stepSec: number): number {
  return 1577826000 + Math.floor((ts - 1577826000) / stepSec) * stepSec;
}

function mskMidnightUnix(ymd: string): number {
  const [year, month, day] = ymd.split('-').map(Number);
  return Math.floor(Date.UTC(year, (month ?? 1) - 1, day ?? 1) / 1000) - MSK_OFFSET_SEC;
}

function dateRangeTouchesNow(to: Date): boolean {
  const today = new Date();
  const toDay = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());
  const todayDay = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return toDay >= todayDay;
}

function matchingWorkloadPreset(from: Date, to: Date): WorkloadPreset | null {
  if (!dateRangeTouchesNow(to)) return null;
  const fromDay = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
  const toDay = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());
  const days = Math.round((toDay - fromDay) / 86_400_000) + 1;
  if (days === 1) return '1d';
  if (days === 5) return '5d';
  if (days === 30) return '1mo';
  if (days === 90) return '3mo';
  return null;
}

function workloadWindowForDates(
  fromDate: Date,
  toDate: Date,
  stepSec: number,
): { from: number; to: number; live: boolean } {
  const from = alignWorkloadBucket(mskMidnightUnix(formatLocalDateParam(fromDate)), stepSec);
  const endExclusive = mskMidnightUnix(formatLocalDateParam(toDate)) + 86_400;
  const live = dateRangeTouchesNow(toDate);
  const to = live ? Math.max(Math.floor(Date.now() / 1000) + 1, from + stepSec) : endExclusive;
  return { from, to, live };
}

function coarsenWorkloadTf(from: number, to: number, tf: WorkloadTf): WorkloadTf {
  const span = Math.max(to - from, 1);
  let index = Math.max(0, WORKLOAD_TF_ORDER.indexOf(tf));
  while (
    index < WORKLOAD_TF_ORDER.length - 1 &&
    span / { '1m': 60, '5m': 300, '15m': 900, '30m': 1800, '1h': 3600, '4h': 14400, '1d': 86400 }[WORKLOAD_TF_ORDER[index]] >
      WORKLOAD_MAX_SERIES_BARS
  ) {
    index += 1;
  }
  return WORKLOAD_TF_ORDER[index];
}

function mergeWorkloadTail(base: WorkloadPointMap, tail: WorkloadPointMap, fromTs: number): WorkloadPointMap {
  const out: WorkloadPointMap = {};
  for (const key of new Set([...Object.keys(base), ...Object.keys(tail)])) {
    const existing = base[key];
    const cut = existing ? existing.t.findIndex((ts) => ts >= fromTs) : -1;
    const keep = cut < 0 ? (existing?.t.length ?? 0) : cut;
    const fresh = tail[key];
    out[key] = {
      t: [...(existing?.t.slice(0, keep) ?? []), ...(fresh?.t ?? [])],
      c: [...(existing?.c.slice(0, keep) ?? []), ...(fresh?.c ?? [])],
      v: [...(existing?.v.slice(0, keep) ?? []), ...(fresh?.v ?? [])],
      r: [...(existing?.r?.slice(0, keep) ?? []), ...(fresh?.r ?? [])],
      p: [...(existing?.p?.slice(0, keep) ?? []), ...(fresh?.p ?? [])],
    };
  }
  return out;
}

function workloadBankName(bankName: string | undefined | null): string {
  return (bankName ?? '').trim();
}

function workloadProfileKey(profile: { key?: string; id: number; source?: string }): string {
  if (profile.key) return profile.key;
  return `qr:${profile.id}`;
}

function workloadPaletteIndex(key: string): number {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  }
  return hash % MULTI_LINE_PALETTE.length;
}

function workloadBanksFromProfiles(profiles: RequisiteWorkloadProfile[]): string[] {
  const set = new Set<string>();
  for (const profile of profiles) {
    const bank = workloadBankName(profile.bank_name);
    if (bank) set.add(bank);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b, 'ru'));
}

function allWorkloadProfileKeys(profiles: RequisiteWorkloadProfile[]): string[] {
  return profiles.map((profile) => workloadProfileKey(profile));
}

function bucketLabel(bucket: string, granularity: 'hour' | 'day'): string {
  if (granularity === 'hour') {
    const match = bucket.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2})/);
    if (match) return `${match[4]}:00`;
    const hour = bucket.match(/T(\d{2}):/)?.[1];
    return hour ? `${hour}:00` : bucket;
  }
  const [year, month, day] = bucket.split('-');
  return year && month && day ? `${day}.${month}` : bucket;
}

function statuses(value?: Partial<CpDashboardChartsPoint['status_counts']>): DashboardStatusValues {
  return {
    closed: Number(value?.closed ?? 0),
    canceled: Number(value?.canceled ?? 0),
    rejected: Number(value?.rejected ?? 0),
    other: Number(value?.other ?? 0),
  };
}

function volumeValues(point: CpDashboardChartsPoint, currency: DashboardCurrency): DashboardStatusValues {
  const source = point.volume_by_status ?? emptyVolumeByStatus();
  return {
    closed: Number(source.closed?.[currency] ?? 0),
    canceled: Number(source.canceled?.[currency] ?? 0),
    rejected: Number(source.rejected?.[currency] ?? 0),
    other: Number(source.other?.[currency] ?? 0),
  };
}

export default function VndDashboardCharts({ sidePanel }: { sidePanel?: ReactNode }) {
  const dashboardFilter = useDashboardFilter();
  const range = dashboardFilter?.range ?? { from: '', to: '' };
  const fromDate = range.from ? parseYmd(range.from) : null;
  const toDate = range.to ? parseYmd(range.to) : null;

  const [workloadMode, setWorkloadMode] = useState<WorkloadChartMode>('conversion');
  const [workloadTf, setWorkloadTf] = useState<WorkloadTf>('1m');
  const [workloadPreset, setWorkloadPreset] = useState<WorkloadPreset | null>('1d');
  const [workloadMetric, setWorkloadMetric] = useState<WorkloadMetric>('amount_vnd');
  const [workloadAverage, setWorkloadAverage] = useState(false);
  const [workloadBusy, setWorkloadBusy] = useState(false);
  const [workloadProfiles, setWorkloadProfiles] = useState<RequisiteWorkloadProfile[]>([]);
  const [workloadPoints, setWorkloadPoints] = useState<WorkloadPointMap>({});
  const [workloadWindow, setWorkloadWindow] = useState<{ from: number; to: number } | null>(null);
  const [workloadSelectedKeys, setWorkloadSelectedKeys] = useState<string[]>([]);
  const [workloadHiddenKeys, setWorkloadHiddenKeys] = useState<string[]>([]);
  const [workloadSelectedBanks, setWorkloadSelectedBanks] = useState<string[]>([]);
  const workloadRequestId = useRef(0);

  const [chartFrame, setChartFrame] = useState<DashboardGranularity>('day');
  const [charts, setCharts] = useState<CpDashboardChartsResponse | null>(null);
  const [volumeCharts, setVolumeCharts] = useState<CpDashboardChartsResponse | null>(null);
  const [chartsBusy, setChartsBusy] = useState(false);
  const [volumeCurrency, setVolumeCurrency] = useState<Exclude<DashboardCurrency, 'rub'>>('vnd');
  const [volumeHiddenIds, setVolumeHiddenIds] = useState<number[]>([]);
  const volumeLegend = useStatusLegendState();
  const statusLegend = useStatusLegendState();

  useEffect(() => {
    if (!fromDate || !toDate) return;
    setWorkloadPreset(matchingWorkloadPreset(fromDate, toDate));
  }, [range.from, range.to]);

  const workloadStepSec = { '1m': 60, '5m': 300, '15m': 900, '30m': 1800, '1h': 3600, '4h': 14400, '1d': 86400 }[workloadTf];
  const workloadResetKey = `${workloadTf}|${range.from}|${range.to}`;
  const workloadLive = Boolean(toDate && dateRangeTouchesNow(toDate));

  useEffect(() => {
    if (!fromDate || !toDate) return;
    const bounds = workloadWindowForDates(fromDate, toDate, workloadStepSec);
    const tf = coarsenWorkloadTf(bounds.from, bounds.to, workloadTf);
    if (tf !== workloadTf) {
      setWorkloadTf(tf);
      return;
    }
    const currentRequest = ++workloadRequestId.current;
    setWorkloadBusy(true);
    setWorkloadPoints({});
    setWorkloadWindow({ from: bounds.from, to: bounds.to });
    void cpApi
      .getDashboardWorkloadSeries({
        from: bounds.from,
        to: bounds.to,
        tf: workloadTf,
        includeProfiles: true,
      })
      .then((response) => {
        if (currentRequest !== workloadRequestId.current) return;
        const profiles = response.profiles ?? [];
        setWorkloadProfiles(profiles);
        setWorkloadPoints(response.series ?? {});
        setWorkloadWindow({ from: bounds.from, to: bounds.to });
        const banks = workloadBanksFromProfiles(profiles);
        const available = new Set(allWorkloadProfileKeys(profiles));
        setWorkloadSelectedBanks((prev) => {
          const kept = prev.filter((bank) => banks.includes(bank));
          return kept.length > 0 ? kept : banks;
        });
        setWorkloadSelectedKeys((prev) => {
          const kept = prev.filter((key) => available.has(key));
          return kept.length > 0 ? kept : allWorkloadProfileKeys(profiles);
        });
        setWorkloadHiddenKeys((prev) => prev.filter((key) => available.has(key)));
      })
      .catch(() => {
        if (currentRequest !== workloadRequestId.current) return;
      })
      .finally(() => {
        if (currentRequest === workloadRequestId.current) setWorkloadBusy(false);
      });
  }, [workloadResetKey, workloadTf, workloadStepSec, range.from, range.to]);

  useEffect(() => {
    if (!workloadWindow || !workloadLive) return;
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      const requestAtStart = workloadRequestId.current;
      const nowSec = Math.floor(Date.now() / 1000);
      const to = nowSec + 1;
      const from = alignWorkloadBucket(nowSec, workloadStepSec) - workloadStepSec;
      void cpApi
        .getDashboardWorkloadSeries({ from, to, tf: workloadTf })
        .then((response) => {
          if (requestAtStart !== workloadRequestId.current) return;
          setWorkloadPoints((prev) => mergeWorkloadTail(prev, response.series ?? {}, from));
          setWorkloadWindow((prev) => (prev ? { ...prev, to } : prev));
        })
        .catch(() => undefined);
    }, WORKLOAD_LIVE_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [Boolean(workloadWindow), workloadLive, workloadStepSec, workloadTf]);

  const multiDay = Boolean(range.from && range.to && range.from !== range.to);
  const chartGranularity: DashboardGranularity = multiDay ? chartFrame : 'hour';

  useEffect(() => {
    if (!range.from || !range.to) return;
    let cancelled = false;
    setChartsBusy(true);
    void cpApi
      .getDashboardCharts({ dateFrom: range.from, dateTo: range.to, granularity: chartGranularity })
      .then((response) => {
        if (cancelled) return;
        setCharts(response);
        setVolumeCharts(response);
        const ids = new Set((response.profiles ?? []).map((item) => item.id));
        setVolumeHiddenIds((prev) => prev.filter((id) => ids.has(id)));
      })
      .catch(() => {
        if (!cancelled) {
          setCharts(null);
          setVolumeCharts(null);
        }
      })
      .finally(() => {
        if (!cancelled) setChartsBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [range.from, range.to, chartGranularity]);

  const volumeProfiles = charts?.profiles ?? [];
  const visibleVolumeIds = useMemo(() => {
    const hidden = new Set(volumeHiddenIds);
    return volumeProfiles.filter((item) => !hidden.has(item.id)).map((item) => item.id);
  }, [volumeHiddenIds, volumeProfiles]);

  useEffect(() => {
    if (!range.from || !range.to) return;
    if (volumeProfiles.length === 0) return;
    const allVisible = visibleVolumeIds.length === volumeProfiles.length;
    if (allVisible) {
      setVolumeCharts(charts);
      return;
    }
    if (visibleVolumeIds.length === 0) {
      setVolumeCharts(charts ? { ...charts, series: charts.series.map((point) => ({
        ...point,
        volume_by_status: emptyVolumeByStatus(),
        total_requests: 0,
        payment_count: 0,
      })) } : null);
      return;
    }
    let cancelled = false;
    void cpApi
      .getDashboardCharts({
        dateFrom: range.from,
        dateTo: range.to,
        granularity: chartGranularity,
        profileIds: visibleVolumeIds,
      })
      .then((response) => {
        if (!cancelled) setVolumeCharts(response);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [range.from, range.to, chartGranularity, visibleVolumeIds.join(','), volumeProfiles.length, charts]);

  const handleWorkloadPresetChange = useCallback(
    (preset: WorkloadPreset) => {
      const days = WORKLOAD_PRESET_DAYS[preset];
      const to = new Date();
      to.setHours(0, 0, 0, 0);
      const from = new Date(to);
      from.setDate(from.getDate() - (days - 1));
      setWorkloadTf(WORKLOAD_PRESET_TF[preset]);
      setWorkloadPreset(preset);
      dashboardFilter?.setRange(formatLocalDateParam(from), formatLocalDateParam(to));
    },
    [dashboardFilter],
  );

  const workloadBankOptions = useMemo(
    () => workloadBanksFromProfiles(workloadProfiles),
    [workloadProfiles],
  );
  const workloadBankFilteredProfiles = useMemo(() => {
    if (workloadSelectedBanks.length === 0) return [];
    const banks = new Set(workloadSelectedBanks);
    const allSelected =
      workloadBankOptions.length > 0 && workloadSelectedBanks.length === workloadBankOptions.length;
    return workloadProfiles.filter((profile) => {
      const bank = workloadBankName(profile.bank_name);
      if (!bank) return allSelected;
      return banks.has(bank);
    });
  }, [workloadProfiles, workloadSelectedBanks, workloadBankOptions]);

  const workloadChartSeries = useMemo(() => {
    const selected = new Set(workloadSelectedKeys);
    const hidden = new Set(workloadHiddenKeys);
    const bankAllowed = new Set(workloadBankFilteredProfiles.map((profile) => workloadProfileKey(profile)));
    return workloadBankFilteredProfiles
      .filter((profile) => selected.has(workloadProfileKey(profile)))
      .map((profile) => {
        const key = workloadProfileKey(profile);
        return {
          id: key,
          name: profile.name,
          color: MULTI_LINE_PALETTE[workloadPaletteIndex(key)],
          visible: !hidden.has(key) && bankAllowed.has(key),
        };
      });
  }, [workloadSelectedKeys, workloadHiddenKeys, workloadBankFilteredProfiles]);

  const handleWorkloadBanksChange = useCallback(
    (banks: string[]) => {
      setWorkloadSelectedBanks(banks);
      if (workloadProfiles.length === 0) {
        setWorkloadSelectedKeys([]);
        return;
      }
      const bankSet = new Set(banks);
      const allSelected =
        workloadBankOptions.length > 0 && banks.length === workloadBankOptions.length;
      const allowedKeys = workloadProfiles
        .filter((profile) => {
          const bank = workloadBankName(profile.bank_name);
          if (!bank) return allSelected;
          return bankSet.has(bank);
        })
        .map((profile) => workloadProfileKey(profile));
      const allowed = new Set(allowedKeys);
      const previousBankSet = new Set(workloadSelectedBanks);
      const previousAllBanks =
        workloadBankOptions.length > 0 && previousBankSet.size === workloadBankOptions.length;
      const previouslyAllowed = new Set(
        workloadProfiles
          .filter((profile) => {
            const bank = workloadBankName(profile.bank_name);
            if (!bank) return previousAllBanks;
            return previousBankSet.has(bank);
          })
          .map((profile) => workloadProfileKey(profile)),
      );
      setWorkloadSelectedKeys((prev) => {
        const kept = prev.filter((key) => allowed.has(key));
        const added = allowedKeys.filter((key) => !previouslyAllowed.has(key));
        const next = [...kept];
        for (const key of added) {
          if (next.includes(key)) continue;
          next.push(key);
        }
        return next.length > 0 ? next : allowedKeys;
      });
      setWorkloadHiddenKeys((prev) => prev.filter((key) => allowed.has(key)));
    },
    [workloadProfiles, workloadBankOptions, workloadSelectedBanks],
  );

  const series = charts?.series ?? [];
  const volumeSeriesSource = volumeCharts?.series ?? [];
  const volumeSeries = volumeSeriesSource.map((point) => ({
    label: bucketLabel(point.bucket, chartGranularity),
    values: volumeValues(point, volumeCurrency),
  }));
  const statusSeries = series.map((point) => ({
    label: bucketLabel(point.bucket, chartGranularity),
    values: statuses(point.status_counts),
  }));
  const paymentConversionSeries = series.map((point) => ({
    label: bucketLabel(point.bucket, chartGranularity),
    value: Number(point.payment_overall_conversion ?? 0) || 0,
  }));
  const volumeFormatter =
    volumeCurrency === 'usdt' ? formatDashboardUsdt : (value: number) => formatDashboardNumber(value, 0);

  const handleVolumeToggle = (id: number, mode: 'toggle' | 'solo' = 'toggle') => {
    const allIds = volumeProfiles.map((item) => item.id);
    if (mode === 'solo') {
      setVolumeHiddenIds((prev) => {
        const hidden = new Set(prev);
        const visible = allIds.filter((item) => !hidden.has(item));
        if (visible.length === 1 && visible[0] === id) return [];
        return allIds.filter((item) => item !== id);
      });
      return;
    }
    setVolumeHiddenIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  if (!range.from || !range.to) return null;

  return (
    <div className="space-y-4">
      <DashboardCard
        title={<WorkloadModePicker value={workloadMode} onChange={setWorkloadMode} />}
        actions={
          <div className="flex h-6 items-center gap-1.5">
            <RequisiteProfileFilter
              profiles={workloadBankFilteredProfiles}
              selectedKeys={workloadSelectedKeys}
              onChange={setWorkloadSelectedKeys}
            />
            <BankFilter
              banks={workloadBankOptions}
              selectedBanks={workloadSelectedBanks}
              onChange={handleWorkloadBanksChange}
            />
            {workloadMode === 'workload' ? (
              <SegmentedControl
                value={workloadMetric}
                options={[
                  { value: 'amount_vnd', label: 'Сумма VND' },
                  { value: 'request_count', label: 'Кол-во' },
                ]}
                onChange={setWorkloadMetric}
              />
            ) : null}
            <WorkloadLegendHelpTip />
          </div>
        }
        trailing={
          <div className="flex h-6 items-center gap-1.5">
            {workloadMode === 'workload' ? (
              <button
                type="button"
                onClick={() => setWorkloadAverage((on) => !on)}
                className={`inline-flex h-6 items-center rounded-md border px-1.5 text-[10px] font-semibold leading-none transition ${
                  workloadAverage
                    ? 'border-indigo-200 bg-indigo-50 text-indigo-800'
                    : 'border-slate-200 bg-slate-100 text-slate-600 hover:text-slate-900'
                }`}
                title="Среднее = сумма / число заявок"
              >
                Среднее
              </button>
            ) : null}
            <WorkloadTimeframePicker value={workloadTf} onChange={(tf) => { setWorkloadPreset(null); setWorkloadTf(tf); }} />
          </div>
        }
      >
        <div className={workloadBusy ? 'opacity-50' : undefined}>
          <WorkloadTimeSeriesChart
            series={workloadChartSeries}
            data={workloadPoints}
            windowFrom={workloadWindow?.from ?? 0}
            windowTo={workloadWindow?.to ?? 0}
            tf={workloadTf}
            metric={workloadMetric}
            average={workloadAverage}
            mode={workloadMode}
            resetKey={workloadResetKey}
            formatValue={
              workloadMode === 'conversion'
                ? formatDashboardPercent
                : workloadAverage || workloadMetric === 'amount_vnd'
                  ? (value) => formatDashboardNumber(value, 0)
                  : formatDashboardNumber
            }
            onToggleSeries={(id, mode = 'toggle') => {
              const allKeys = workloadChartSeries.map((item) => item.id);
              if (mode === 'solo') {
                setWorkloadHiddenKeys((prev) => {
                  const hidden = new Set(prev);
                  const visible = allKeys.filter((key) => !hidden.has(key));
                  if (visible.length === 1 && visible[0] === id) return [];
                  return allKeys.filter((key) => key !== id);
                });
                return;
              }
              setWorkloadHiddenKeys((prev) =>
                prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
              );
            }}
          />
          <div className="mt-2 flex items-center justify-between gap-2 sm:pr-[21.25rem]">
            <WorkloadPresetPicker value={workloadPreset} onChange={handleWorkloadPresetChange} />
            <span className="text-[10px] font-medium text-slate-400">Период как в шапке · колесо — масштаб</span>
          </div>
        </div>
      </DashboardCard>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <DashboardCard
          title={`Объём, ${volumeCurrency.toUpperCase()}`}
          legend={<StatusLegend enabled={volumeLegend.enabled} onToggle={volumeLegend.toggle} />}
          actions={
            <SegmentedControl
              value={volumeCurrency}
              options={[
                { value: 'vnd', label: 'VND' },
                { value: 'usdt', label: 'USDT' },
              ]}
              onChange={setVolumeCurrency}
            />
          }
          trailing={<ChartFrameToggle visible={multiDay} value={chartFrame} onChange={setChartFrame} />}
        >
          <div className={`flex min-w-0 gap-3 ${chartsBusy ? 'opacity-50' : ''}`}>
            <div className="min-w-0 flex-1">
              <StackedBarChart
                data={volumeSeries}
                formatValue={volumeFormatter}
                legend={false}
                enabledStatuses={volumeLegend.enabled}
                onToggleStatus={volumeLegend.toggle}
                granularity={chartGranularity}
              />
            </div>
            <VolumeProfileLegend
              profiles={volumeProfiles}
              hiddenIds={volumeHiddenIds}
              currency={volumeCurrency}
              onToggle={handleVolumeToggle}
            />
          </div>
        </DashboardCard>

        <DashboardCard
          title="Заявки по статусам"
          legend={<StatusLegend enabled={statusLegend.enabled} onToggle={statusLegend.toggle} />}
          trailing={<ChartFrameToggle visible={multiDay} value={chartFrame} onChange={setChartFrame} />}
        >
          <div className={chartsBusy ? 'opacity-50' : undefined}>
            <StackedBarChart
              data={statusSeries}
              formatValue={formatDashboardNumber}
              legend={false}
              enabledStatuses={statusLegend.enabled}
              onToggleStatus={statusLegend.toggle}
              granularity={chartGranularity}
            />
          </div>
        </DashboardCard>

        <DashboardCard
          className="h-full"
          title="Конверсия оплат"
          trailing={<ChartFrameToggle visible={multiDay} value={chartFrame} onChange={setChartFrame} />}
        >
          <div className={chartsBusy ? 'opacity-50' : undefined}>
            <LineChart
              data={paymentConversionSeries}
              formatValue={formatDashboardPercent}
              formatAxisValue={formatDashboardPercent}
              color="#7c3aed"
              granularity={chartGranularity}
            />
          </div>
        </DashboardCard>

        {sidePanel ? <div className="flex min-h-0 min-w-0 flex-col gap-4">{sidePanel}</div> : null}
      </div>
    </div>
  );
}
